/**
 * Module-level bridge between the Pusher handlers (Redux thunks dispatched by
 * DashboardLayout, outside React Query) and the Inicio screen:
 * - an event bus: every task event tells the screen and the nav badge to
 *   refetch;
 * - the list of the last offered tasks, which keeps a repeated Pusher offer
 *   from toasting twice.
 * Both live for the page's life, like the Pusher connection itself.
 */

const MAX_NOTIFICATIONS = 5;

const eventListeners = new Set();
const notificationListeners = new Set();
let notifications = [];

export const subscribeHumanTaskEvents = (listener) => {
  eventListeners.add(listener);
  return () => eventListeners.delete(listener);
};

export const emitHumanTaskEvent = (event, data) => {
  eventListeners.forEach((listener) => listener(event, data));
};

export const getTaskNotifications = () => notifications;

export const subscribeTaskNotifications = (listener) => {
  notificationListeners.add(listener);
  return () => notificationListeners.delete(listener);
};

const setNotifications = (next) => {
  notifications = next;
  notificationListeners.forEach((listener) => listener(notifications));
};

/**
 * Adds an offered task at the top. Returns `false` (and changes nothing) when
 * that task is already listed: Pusher may deliver the same offer twice.
 */
export const addTaskNotification = (notification) => {
  if (notifications.some((item) => item.taskId === notification.taskId)) return false;
  setNotifications([notification, ...notifications].slice(0, MAX_NOTIFICATIONS));
  return true;
};

export const removeTaskNotification = (taskId) => {
  if (!notifications.some((item) => item.taskId === taskId)) return;
  setNotifications(notifications.filter((item) => item.taskId !== taskId));
};

export const clearTaskNotifications = () => setNotifications([]);

/**
 * The open chat shows offers itself (spec S3: the in-chat toast with «Tomar»).
 * While a chat registers here, `taskOffered` goes to it instead of the generic
 * toast; any other screen keeps the generic toast. One chat at a time: the
 * last one registered wins, and unregistering only removes itself.
 * `presenter` = { show(offer), withdraw(taskId) }.
 */
let chatOfferPresenter = null;

export const setChatTaskOfferPresenter = (presenter) => {
  chatOfferPresenter = presenter;
  return () => {
    if (chatOfferPresenter === presenter) chatOfferPresenter = null;
  };
};

/** `true` when an open chat took the offer; `false` sends it to the generic toast. */
export const presentTaskOfferInChat = (offer) => {
  if (!chatOfferPresenter) return false;
  chatOfferPresenter.show(offer);
  return true;
};

/** The offer left the list (expired, taken, reassigned): the chat closes its toast. */
export const withdrawTaskOfferInChat = (taskId) => {
  if (chatOfferPresenter && taskId) chatOfferPresenter.withdraw(taskId);
};
